import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

/** Below this column width a mock switches to its narrow layout. */
const NARROW_BELOW = 600

interface MockScaleProps {
    children: ReactNode
    /** The width the mock is designed at, in px. It is scaled to fit its column. */
    designWidth: number
    /**
     * The width of the mock's narrow layout, used when the column is under 600px. The mock is
     * then drawn at this width and scaled to fit, so it is never cut off. Mocks style their
     * narrow layout with container variants such as `@max-[600px]:`.
     */
    narrowWidth?: number
    className?: string
}

/**
 * Renders a product mock at its design size and scales it to fit the column, so type and
 * spacing keep their proportions at every width. The scaled box is a size container, so the
 * mock inside can switch to a narrow layout with container variants.
 */
export const MockScale: FC<MockScaleProps> = ({ children, designWidth, narrowWidth, className }): ReactNode => {
    const outerRef = useRef<HTMLDivElement>(null)
    const innerRef = useRef<HTMLDivElement>(null)
    const [box, setBox] = useState({ scale: 1, width: designWidth, height: 0 })

    useEffect(() => {
        const outer = outerRef.current
        const inner = innerRef.current

        if (!outer || !inner) return

        const measure = () => {
            const available = outer.clientWidth

            if (!available) return

            const width = narrowWidth && available < NARROW_BELOW ? narrowWidth : designWidth

            setBox(previous => {
                const next = { scale: available / width, width, height: inner.offsetHeight * (available / width) }

                return previous.scale === next.scale && previous.width === next.width && previous.height === next.height ? previous : next
            })
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(outer)
        observer.observe(inner)

        return () => observer.disconnect()
    }, [designWidth, narrowWidth])

    return (
        <div ref={outerRef} className={cn('relative w-full overflow-hidden', className)} style={{ height: box.height || undefined }}>
            <div
                ref={innerRef}
                className={cn('@container absolute left-0 top-0 origin-top-left will-change-transform', !box.height && 'invisible')}
                style={{ width: box.width, transform: `scale(${box.scale})` }}
            >
                {children}
            </div>
        </div>
    )
}
